import * as React from 'react';
import { useSearch } from '@elastic/react-search-ui';
import FilterWrapper from '../../FilterWrapper';
import { readChartRows } from '../../chartRows';
import { groupedOptionsOf } from '../../../helpers';

// Sidebar control for the `kind: "grouped-dropdown"` filter: a single dropdown
// whose options are organised into named sections, each drawn from a distinct
// data field. Selecting an option applies a live search filter on that section's
// field and clears all other sections' filters so the chart never mixes shapes.
// Options come from the full injected payload scoped to the active scope field.
export function GroupedDropdownFilter({
  label,
  sections: sectionsProp,
  valueLabels,
  scopeAttribute,
  collectionName,
  sharedSelection = null,
}) {
  const { filters, setFilter, removeFilter } = useSearch();

  // Normalize here too — a parameter default only covers `undefined`, and an
  // imported config can carry `sections: null`.
  const sections = React.useMemo(
    () => (Array.isArray(sectionsProp) ? sectionsProp : []),
    [sectionsProp]
  );

  const allRows = React.useMemo(() => readChartRows(collectionName) || [], [collectionName]);

  const scopeValue = scopeAttribute
    ? filters?.find(f => f.field === scopeAttribute)?.values?.[0]
    : null;
  const rows = React.useMemo(
    // Compare as strings: the scope field is admin-chosen and may be numeric in
    // the payload while search-ui holds its filter value as a string (the
    // in-memory connector's matchFilter does the same).
    () => (scopeValue != null ? allRows.filter(r => String(r?.[scopeAttribute]) === String(scopeValue)) : allRows),
    [allRows, scopeValue, scopeAttribute]
  );

  const options = React.useMemo(
    () => groupedOptionsOf(rows, { sections, valueLabels }),
    [rows, sections, valueLabels]
  );
  const optionsKey = options.map(o => o.key).join(',');

  // Derive current selection: the first section field that has an active filter.
  const current = React.useMemo(() => {
    for (const section of sections) {
      const selected = filters?.find(f => f.field === section.field)?.values?.[0];
      if (selected) return `${section.field}:${selected}`;
    }
    return '';
  }, [filters, sections]);

  const apply = React.useCallback((option) => {
    sections.forEach(s => { if (s.field !== option.field) removeFilter(s.field); });
    setFilter(option.field, option.value, 'any');
  }, [sections, removeFilter, setFilter]);

  // Restore a shared-permalink selection once, before the default snap can run —
  // and only once it's valid for the active scope (the model may still be
  // settling). Until then, hold off snapping so we don't grab a default and lose it.
  const sharedPending = React.useRef(Boolean(sharedSelection));

  // Snap to the first available option when nothing is selected or the current
  // selection has no data for the active scope.
  React.useEffect(() => {
    if (options.length === 0) return;
    if (sharedPending.current) {
      // Release the hold on the first run with real options, hit or miss. If the
      // shared value isn't valid for this scope (stale permalink, republished
      // data) we must fall through to the default snap — leaving the flag set
      // would block snapping forever, so no section filter is ever applied and
      // the chart plots every section at once while the select shows option 0.
      sharedPending.current = false;
      const target = options.find(o => o.key === sharedSelection);
      if (target) {
        apply(target);
        return;
      }
    }
    if (!current || !options.some(o => o.key === current)) {
      apply(options[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [optionsKey]);

  // Render even with a single option: the filter above is applied either way, and
  // the one option is what the chart is scoped to. Hiding it left a model whose
  // rows are all one configuration — an agentic-only model, say — looking as if
  // it had no configuration at all. Only a genuinely empty list is dropped.
  if (options.length === 0) return null;

  // De-duplicated: options are matched to a group BY LABEL, so two sections
  // sharing a label would otherwise render two <optgroup>s with the same React
  // key, each holding the union of both sections' options (every option twice).
  const sectionLabels = [...new Set(sections.map(s => s.label).filter(Boolean))];

  return (
    <FilterWrapper label={label} attribute='grouped-dropdown' kind='dropdown'>
      <select
        className='nv-chart__facet-select'
        aria-label={label}
        value={current || options[0].key}
        onChange={(event) => {
          const option = options.find(o => o.key === event.target.value);
          if (option) apply(option);
        }}
      >
        {sectionLabels.map(group => {
          const items = options.filter(o => o.group === group);
          if (items.length === 0) return null;
          return (
            <optgroup key={group} label={group}>
              {items.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
            </optgroup>
          );
        })}
      </select>
    </FilterWrapper>
  );
}
